import { AnimatePresence, motion } from 'motion/react';
import { Database, FileCode2, Layers3, LayoutDashboard, LogOut, RadioTower, Settings, ShieldCheck } from 'lucide-react';
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from 'react';

import { api, setUnauthorizedHandler } from './api';
import { Button, Spinner } from './components/ui';
import { errorMessage } from './lib';
import { CollectionsPage } from './pages/CollectionsPage';
import { FilesPage } from './pages/FilesPage';
import { OverviewPage } from './pages/OverviewPage';
import { PublicationsPage } from './pages/PublicationsPage';
import { SettingsPage } from './pages/SettingsPage';
import { SourcesPage } from './pages/SourcesPage';
import type { Session } from './types';

type PageId = 'overview' | 'sources' | 'collections' | 'files' | 'publications' | 'settings';

interface PageItem { id: PageId; label: string; icon: ReactNode }

const pages: PageItem[] = [
  { id: 'overview', label: '概览', icon: <LayoutDashboard size={19} /> },
  { id: 'sources', label: '订阅源', icon: <Database size={19} /> },
  { id: 'collections', label: '组合', icon: <Layers3 size={19} /> },
  { id: 'files', label: '文件', icon: <FileCode2 size={19} /> },
  { id: 'publications', label: '发布', icon: <RadioTower size={19} /> },
  { id: 'settings', label: '设置', icon: <Settings size={19} /> },
];

function NavigationButton({ item, active, mobile = false, onSelect }: { item: PageItem; active: boolean; mobile?: boolean; onSelect: () => void }) {
  return <motion.button
    className={active ? 'active' : ''}
    onClick={onSelect}
    whileTap={{ scale: 0.97 }}
    transition={{ type: 'spring', stiffness: 500, damping: 34 }}
  >
    {active && <motion.span className="nav-indicator" layoutId={mobile ? 'bottom-nav-indicator' : 'sidebar-nav-indicator'} transition={{ type: 'spring', stiffness: 420, damping: 34 }} />}
    <span className="nav-content">{item.icon}<span>{item.label}</span></span>
  </motion.button>;
}

function Login({ onSuccess }: { onSuccess: (session: Session) => void }) {
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await api.post('/api/auth/login', { password });
      onSuccess(await api.get<Session>('/api/auth/session'));
    } catch (caught) {
      setError(errorMessage(caught));
      // Clear the rejected password and return focus so a retry is immediate.
      setPassword('');
      inputRef.current?.focus();
    } finally {
      setSubmitting(false);
    }
  };

  return <main className="login-page">
    <motion.section className="login-panel" initial={{ opacity: 0, y: 18, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ type: 'spring', stiffness: 240, damping: 28 }}>
      <div className="login-seal-wrap">
        <motion.div className="brand-mark" aria-hidden="true" initial={{ opacity: 0, scale: 1.7, rotate: -10 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} transition={{ delay: 0.22, type: 'spring', stiffness: 560, damping: 21 }}>订</motion.div>
        <motion.span className="seal-ripple" initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: [0, 0.6, 0], scale: [0.7, 1.45, 1.6] }} transition={{ delay: 0.3, duration: 0.9, ease: 'easeOut' }} />
      </div>
      <motion.div className="login-heading" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.34, duration: 0.32, ease: [0.22, 1, 0.36, 1] }}><span>EDGESUB · CONSOLE</span><h1>订阅，编而有序。</h1><p>登录私人控制台，编排来源、组合与发布地址。</p></motion.div>
      <motion.form onSubmit={submit} className="login-form" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.42, duration: 0.32, ease: [0.22, 1, 0.36, 1] }}>
        <label className="field"><span className="field-label">管理密码</span><input ref={inputRef} autoFocus type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="输入 ADMIN_PASSWORD" autoComplete="current-password" required /></label>
        {error && <div className="form-error" role="alert">{error}</div>}
        <Button type="submit" disabled={submitting || !password}>{submitting ? '正在验证...' : '进入控制台'}</Button>
      </motion.form>
      <motion.div className="login-security" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55, duration: 0.3 }}><ShieldCheck size={16} /><span>凭据仅发送到当前 Worker</span></motion.div>
    </motion.section>
  </main>;
}

export function App() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [page, setPage] = useState<PageId>('overview');

  useEffect(() => {
    api.get<Session>('/api/auth/session').then(setSession).catch(() => setSession(null));
  }, []);

  useEffect(() => {
    // An expired session mid-action returns the user to the login screen.
    setUnauthorizedHandler(() => setSession(null));
    return () => setUnauthorizedHandler(null);
  }, []);

  if (session === undefined) return <div className="app-loading"><Spinner label="正在连接 EdgeSub" /></div>;
  if (!session?.authenticated) return <Login onSuccess={setSession} />;

  const logout = async () => {
    try { await api.post('/api/auth/logout'); } finally { setSession(null); }
  };

  // Each page refetches on mount and after its own mutations, and switching pages
  // remounts via the AnimatePresence key below — so no global version counter is
  // needed. (The old counter also remounted the whole page, which closed open
  // dialogs whenever a background refresh completed.)
  const content = {
    overview: <OverviewPage />,
    sources: <SourcesPage />,
    collections: <CollectionsPage />,
    files: <FilesPage />,
    publications: <PublicationsPage />,
    settings: <SettingsPage sourceCodeUrl={session.sourceCodeUrl} />,
  }[page];

  return <div className="app-shell">
    <aside className="sidebar">
      <div className="brand"><span className="brand-mark small" aria-hidden="true">订</span><div><strong>EdgeSub</strong><span>私人订阅台</span></div></div>
      <nav>{pages.map((item) => <NavigationButton key={item.id} item={item} active={page === item.id} onSelect={() => setPage(item.id)} />)}</nav>
      <div className="sidebar-foot"><button onClick={() => void logout()}><LogOut size={18} /><span>退出登录</span></button><span>v0.1.0</span></div>
    </aside>
    <main className="main-area">
      <header className="mobile-header"><div className="brand"><span className="brand-mark small" aria-hidden="true">订</span><strong>EdgeSub</strong></div><div className="mobile-header-actions"><button className="icon-button" aria-label="设置" title="设置" onClick={() => setPage('settings')}><Settings size={18} /></button><button className="icon-button" aria-label="退出登录" title="退出登录" onClick={() => void logout()}><LogOut size={18} /></button></div></header>
      <AnimatePresence mode="wait">
        <motion.div key={page} className="page-frame" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}>{content}</motion.div>
      </AnimatePresence>
    </main>
    <nav className="bottom-nav">{pages.filter((item) => item.id !== 'settings').map((item) => <NavigationButton key={item.id} item={item} active={page === item.id} mobile onSelect={() => setPage(item.id)} />)}</nav>
  </div>;
}
